import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { NumPad } from '@/components/NumPad';
import { Mail, User, Eye, EyeOff } from 'lucide-react';
import type { LoginFormProps, LoginFormData, LoginFormErrors } from './LoginForm.types';

type LoginMode = 'email' | 'cashier';

export const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  isLoading = false,
  error = null,
}) => {
  const [loginMode, setLoginMode] = useState<LoginMode>('email');
  const [formData, setFormData] = useState<LoginFormData>({
    username: '',
    password: '',
  });
  const [cashierCode, setCashierCode] = useState('');
  const [cashierPin, setCashierPin] = useState('');
  const [activeField, setActiveField] = useState<'code' | 'pin'>('code');
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
  const usernameRef = useRef<HTMLInputElement>(null);

  // Auto-focus en el campo usuario cuando está en modo email
  useEffect(() => {
    if (loginMode === 'email' && usernameRef.current) {
      usernameRef.current.focus();
    }
  }, [loginMode]);

  // Limpiar campos al cambiar de modo
  useEffect(() => {
    setFormData({ username: '', password: '' });
    setCashierCode('');
    setCashierPin('');
    setFormErrors({});
    setActiveField('code');
  }, [loginMode]);

  const validateForm = (): boolean => {
    const errors: LoginFormErrors = {};

    if (loginMode === 'email') {
      if (!formData.username.trim()) {
        errors.username = 'El email es requerido';
      }
      if (!formData.password.trim()) {
        errors.password = 'La contraseña es requerida';
      }
    } else {
      if (!cashierCode.trim() || cashierCode.length !== 8) {
        errors.username = 'El código debe tener 8 dígitos';
      }
      if (!cashierPin.trim() || cashierPin.length !== 6) {
        errors.password = 'El PIN debe tener 6 dígitos';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (loginMode === 'email') {
      await onLogin(formData.username, formData.password);
    } else {
      // Para modo cajero, enviar el código como username y PIN como password
      await onLogin(cashierCode, cashierPin);
    }
  };

  const handleInputChange = (field: keyof LoginFormData) => (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value,
    }));

    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleNumPadKeyPress = (key: string) => {
    if (activeField === 'code') {
      if (cashierCode.length < 8) {
        setCashierCode(prev => prev + key);
        if (formErrors.username) {
          setFormErrors(prev => ({ ...prev, username: undefined }));
        }
      }
      // Auto-cambiar al PIN cuando el código esté completo
      if (cashierCode.length === 7) {
        setTimeout(() => setActiveField('pin'), 100);
      }
    } else {
      if (cashierPin.length < 6) {
        setCashierPin(prev => prev + key);
        if (formErrors.password) {
          setFormErrors(prev => ({ ...prev, password: undefined }));
        }
      }
    }
  };

  const handleNumPadDelete = () => {
    if (activeField === 'code') {
      setCashierCode(prev => prev.slice(0, -1));
    } else {
      setCashierPin(prev => prev.slice(0, -1));
    }
  };

  const canSubmitCashier = cashierCode.length === 8 && cashierPin.length === 6;

  return (
    <div className="w-full max-w-md mx-auto">
      <Card className="shadow-lg">
        <CardContent>
          {/* Logo de Macartur */}
          <img
            src="/logo-macartur.png"
            alt="Macartur"
            className="mx-auto w-24 h-24 object-contain mb-4"
          />
          {/* Tabs para modo de login */}
          <div className="flex mb-6 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => setLoginMode('email')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-sm font-medium transition-all ${
                loginMode === 'email'
                  ? 'bg-white text-macartur-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Mail className="w-4 h-4" />
              Correo
            </button>
            <button
              type="button"
              onClick={() => setLoginMode('cashier')}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-md text-sm font-medium transition-all ${
                loginMode === 'cashier'
                  ? 'bg-white text-macartur-primary shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <User className="w-4 h-4" />
              Cajero
            </button>
          </div>

          {loginMode === 'email' ? (
            /* Formulario de Email */
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-macartur-accent1 mb-1">
                  Email
                </label>
                <Input
                  ref={usernameRef}
                  id="username"
                  type="email"
                  placeholder="Ingresa tu email"
                  value={formData.username}
                  onChange={handleInputChange('username')}
                  className={formErrors.username ? 'border-red-500' : ''}
                  disabled={isLoading}
                />
                {formErrors.username && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-macartur-accent1 mb-1">
                  Contraseña
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Ingresa tu contraseña"
                    value={formData.password}
                    onChange={handleInputChange('password')}
                    className={formErrors.password ? 'border-red-500 pr-10' : 'pr-10'}
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                )}
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3" role="alert">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12"
                disabled={isLoading}
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </Button>
            </form>
          ) : (
            /* Formulario de Cajero con NumPad */
            <div className="space-y-4">
              {/* Campo Código de Cajero */}
              <div>
                <label className="block text-sm font-medium text-macartur-accent1 mb-1">
                  Código de Cajero
                </label>
                <div
                  onClick={() => setActiveField('code')}
                  className={`w-full h-12 px-4 rounded-md border-2 flex items-center justify-center text-2xl font-mono tracking-widest cursor-pointer transition-colors ${
                    activeField === 'code'
                      ? 'border-macartur-primary bg-orange-50'
                      : 'border-gray-200 bg-white'
                  } ${formErrors.username ? 'border-red-500' : ''}`}
                >
                  {cashierCode || <span className="text-gray-300">________</span>}
                </div>
                {formErrors.username && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.username}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">8 dígitos</p>
              </div>

              {/* Campo PIN */}
              <div>
                <label className="block text-sm font-medium text-macartur-accent1 mb-1">
                  PIN
                </label>
                <div
                  onClick={() => setActiveField('pin')}
                  className={`w-full h-12 px-4 rounded-md border-2 flex items-center justify-center text-2xl font-mono tracking-widest cursor-pointer transition-colors ${
                    activeField === 'pin'
                      ? 'border-macartur-primary bg-orange-50'
                      : 'border-gray-200 bg-white'
                  } ${formErrors.password ? 'border-red-500' : ''}`}
                >
                  {'●'.repeat(cashierPin.length) || <span className="text-gray-300">______</span>}
                </div>
                {formErrors.password && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.password}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">6 dígitos</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3" role="alert">
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}

              {/* Teclado Numérico */}
              <div className="pt-2">
                <NumPad
                  onKeyPress={handleNumPadKeyPress}
                  onDelete={handleNumPadDelete}
                  onSubmit={handleSubmit}
                  showSubmit={canSubmitCashier}
                  disabled={isLoading}
                />
              </div>

              {/* Botón de submit alternativo */}
              {canSubmitCashier && (
                <Button
                  type="button"
                  onClick={() => handleSubmit()}
                  className="w-full h-12 mt-4"
                  disabled={isLoading}
                >
                  {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-center text-xs text-gray-400 mt-4">v1.3.12</p>
    </div>
  );
};
